import React, { useCallback, useMemo } from "react";
import {
    AccountingReportComparison,
    AccountingReportComparisonDiff,
    AccountingReportRatioType,
} from "@common/constants";
import { isEnumMember } from "@common/helpers/typeGuards";
import { AccountingReportComparisonOptions } from "@common/types/reports/report-type";
import { Picker } from "@design-system/Picker/Picker.lib";
import type { PickerSectionData } from "@design-system/Picker/Picker.types";

const PICKER_SECTIONS = {
    comparisons: {
        title: "Compare to",
        prefix: "comp:",
        enumObject: AccountingReportComparison,
        labels: {
            [AccountingReportComparison.PREVIOUS_YEAR]: "Previous year (PY)",
            [AccountingReportComparison.PREVIOUS_PERIOD]:
                "Previous period (PP)",
        },
    },
    comparisonDiffs: {
        title: "Variance",
        prefix: "diff:",
        enumObject: AccountingReportComparisonDiff,
        labels: {
            [AccountingReportComparisonDiff.PERCENTAGE]: "Percentage (%)",
            [AccountingReportComparisonDiff.ABSOLUTE]: "Amount ($)",
        },
    },
    ratioTypes: {
        title: "Proportion",
        prefix: "ratio:",
        enumObject: AccountingReportRatioType,
        labels: {
            [AccountingReportRatioType.INCOME_PERCENTAGE]: "% of income",
            [AccountingReportRatioType.EXPENSE_PERCENTAGE]: "% of expense",
        },
    },
};

interface ComparisonPickerSection<T extends string> {
    title: string;
    prefix: string;
    enumObject: Record<string, T>;
    labels: Record<T, string>;
}

function toSection<T extends string>({
    title,
    prefix,
    labels,
    values,
    isDisabled,
}: ComparisonPickerSection<T> & {
    values: readonly T[];
    isDisabled?: boolean;
}): PickerSectionData<string> {
    return {
        title,
        items: values.map((value) => ({
            value: `${prefix}${value}`,
            label: labels[value],
            disabled: isDisabled,
        })),
    };
}

function withPrefix(prefix: string, values: string[]): string[] {
    return values.map((value) => `${prefix}${value}`);
}

function decodeSection<T extends string>(
    values: string[],
    { prefix, enumObject }: ComparisonPickerSection<T>,
): T[] {
    return values
        .filter((value) => value.startsWith(prefix))
        .map((value) => value.slice(prefix.length))
        .filter(isEnumMember(enumObject));
}

function decodeValues(values: string[]) {
    return {
        comparisons: decodeSection(values, PICKER_SECTIONS.comparisons),
        comparisonDiffs: decodeSection(values, PICKER_SECTIONS.comparisonDiffs),
        ratioTypes: decodeSection(values, PICKER_SECTIONS.ratioTypes),
    };
}

interface ComparisonSelectProps {
    comparisons: AccountingReportComparison[];
    comparisonDiffs: AccountingReportComparisonDiff[];
    ratioTypes: AccountingReportRatioType[];
    onChange: (value: {
        comparisons: AccountingReportComparison[];
        comparisonDiffs: AccountingReportComparisonDiff[];
        ratioTypes: AccountingReportRatioType[];
    }) => void;
    availableOptions: AccountingReportComparisonOptions;
    disabled?: boolean;
    className?: string;
    id?: string;
}

export const AccountingReportComparisonSelect: React.FC<
    ComparisonSelectProps
> = ({
    comparisons,
    comparisonDiffs,
    ratioTypes,
    onChange,
    availableOptions: {
        comparisons: availableComparisons,
        comparisonDiffs: availableComparisonDiffs,
        ratioTypes: availableRatioTypes,
    },
    disabled = false,
    className,
    id,
}) => {
    const combinedValues = useMemo<string[]>(
        () => [
            ...withPrefix(PICKER_SECTIONS.comparisons.prefix, comparisons),
            ...withPrefix(
                PICKER_SECTIONS.comparisonDiffs.prefix,
                comparisonDiffs,
            ),
            ...withPrefix(PICKER_SECTIONS.ratioTypes.prefix, ratioTypes),
        ],
        [comparisons, comparisonDiffs, ratioTypes],
    );

    const hasComparisons = comparisons.length > 0;
    const hasRatioOptions = availableRatioTypes.length > 0;

    const sections = useMemo<Array<PickerSectionData<string>>>(
        () => [
            ...(hasRatioOptions
                ? [
                      toSection({
                          ...PICKER_SECTIONS.ratioTypes,
                          values: availableRatioTypes,
                      }),
                  ]
                : []),
            toSection({
                ...PICKER_SECTIONS.comparisons,
                values: availableComparisons,
            }),
            toSection({
                ...PICKER_SECTIONS.comparisonDiffs,
                values: availableComparisonDiffs,
                isDisabled: !hasComparisons,
            }),
        ],
        [
            availableComparisons,
            availableComparisonDiffs,
            availableRatioTypes,
            hasRatioOptions,
            hasComparisons,
        ],
    );

    const handleValueChange = useCallback(
        (values: string[]) => {
            const decoded = decodeValues(values);

            onChange({
                comparisons: decoded.comparisons,
                comparisonDiffs:
                    decoded.comparisons.length === 0
                        ? []
                        : decoded.comparisonDiffs,
                ratioTypes: hasRatioOptions ? decoded.ratioTypes : ratioTypes,
            });
        },
        [onChange, hasRatioOptions, ratioTypes],
    );

    return (
        <Picker.Select
            id={id}
            multiple
            sections={sections}
            value={combinedValues}
            onValueChange={handleValueChange}
            disabled={disabled}
            placeholder="Compare"
            renderValue={(values) => `Compare (${values.length})`}
            trigger={
                <Picker.Button
                    variant="primary"
                    size="md"
                    className={className}
                />
            }
        />
    );
};
